import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { listProgressionEntries } from '../../lib/last-played';
import { buildSessionDuJour, } from '../../lib/session-du-jour';
import { CATEGORY_BY_SLUG } from '../../lib/categories';
import { loadProgression } from '../../lib/game-progression';
import { SessionIntermission } from './SessionIntermissions';
import { SessionSummary } from './SessionSummary';
import { CategoryIcon } from '../../components/CategoryIcon';
import { SectionMarker } from '../../components/SectionMarker';
import { CoordinateMarker } from '../../components/CoordinateMarker';
import { StickyCta } from '../../components/StickyCta';
const STORAGE_KEY = 'labrain.session.active';
function readPersisted() {
    if (typeof window === 'undefined')
        return null;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed.items) || typeof parsed.currentIndex !== 'number') {
            return null;
        }
        return parsed;
    }
    catch {
        return null;
    }
}
function writePersisted(state) {
    if (typeof window === 'undefined')
        return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
    catch {
        // ignore — sessionStorage unavailable
    }
}
function clearPersisted() {
    if (typeof window === 'undefined')
        return;
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    }
    catch {
        // ignore
    }
}
function humanizeSlug(slug) {
    return slug
        .split('-')
        .map((p) => (p.length > 0 ? p[0].toUpperCase() + p.slice(1) : p))
        .join(' ');
}
function newSessionId() {
    return `s${Date.now().toString(36)}`;
}
export function SessionPlayer() {
    const { t } = useTranslation(['session', 'common']);
    const navigate = useNavigate();
    // Init : restore from sessionStorage si existe et toujours valide,
    // sinon génère nouvelle playlist.
    const [session, setSession] = useState(() => {
        const persisted = readPersisted();
        if (persisted && persisted.items.length > 0) {
            return persisted;
        }
        const items = buildSessionDuJour(listProgressionEntries());
        const fresh = {
            id: newSessionId(),
            items,
            currentIndex: 0,
            snapshotAt: null,
            pendingSlug: null,
        };
        writePersisted(fresh);
        return fresh;
    });
    const [phase, setPhase] = useState(() => {
        const persisted = readPersisted();
        return persisted?.snapshotAt ? 'redirect' : 'intermission';
    });
    // Auto-advance check : si on a un snapshotAt + pendingSlug, et que la
    // progression du jeu pendingSlug a été mise à jour APRÈS snapshotAt,
    // alors le user est revenu après avoir joué → advance.
    useEffect(() => {
        if (!session.snapshotAt || !session.pendingSlug)
            return;
        const prog = loadProgression(session.pendingSlug);
        if (!prog.lastRunAt)
            return;
        if (Date.parse(prog.lastRunAt) <= Date.parse(session.snapshotAt))
            return;
        // User a joué — auto-advance.
        const nextIdx = session.currentIndex + 1;
        if (nextIdx >= session.items.length) {
            setPhase('summary');
            const updated = { ...session, currentIndex: nextIdx, snapshotAt: null, pendingSlug: null };
            setSession(updated);
            writePersisted(updated);
            return;
        }
        const updated = {
            ...session,
            currentIndex: nextIdx,
            snapshotAt: null,
            pendingSlug: null,
        };
        setSession(updated);
        writePersisted(updated);
        setPhase('intermission');
    }, [session]);
    const current = session.items[session.currentIndex];
    const onAbandon = useCallback(() => {
        clearPersisted();
        navigate('/');
    }, [navigate]);
    const onStartCurrent = useCallback(() => {
        if (!current)
            return;
        const updated = {
            ...session,
            snapshotAt: new Date().toISOString(),
            pendingSlug: current.slug,
        };
        setSession(updated);
        writePersisted(updated);
        setPhase('redirect');
    }, [current, session]);
    const onSkip = useCallback(() => {
        const nextIdx = session.currentIndex + 1;
        if (nextIdx >= session.items.length) {
            setPhase('summary');
            const updated = { ...session, currentIndex: nextIdx, snapshotAt: null, pendingSlug: null };
            setSession(updated);
            writePersisted(updated);
            return;
        }
        const updated = {
            ...session,
            currentIndex: nextIdx,
            snapshotAt: null,
            pendingSlug: null,
        };
        setSession(updated);
        writePersisted(updated);
        setPhase('intermission');
    }, [session]);
    const onSummaryClose = useCallback(() => {
        clearPersisted();
        navigate('/');
    }, [navigate]);
    const completedCount = useMemo(() => Math.min(session.currentIndex, session.items.length), [session.currentIndex, session.items.length]);
    if (phase === 'summary' || session.currentIndex >= session.items.length) {
        return (_jsx(SessionSummary, { playlist: session.items, onClose: onSummaryClose }));
    }
    if (!current) {
        return null;
    }
    if (phase === 'intermission') {
        return (_jsx(SessionIntermission, { item: current, position: session.currentIndex, total: session.items.length, onStart: onStartCurrent, onSkip: onSkip, onAbandon: onAbandon }));
    }
    // phase === 'redirect' : on attend que le user lance + revienne.
    // L'auto-advance se fera au remount via le useEffect.
    const cat = CATEGORY_BY_SLUG[current.category];
    return (_jsxs("section", { className: "flex flex-1 flex-col gap-7 pt-2 pb-4", children: [_jsxs("header", { className: "flex items-center justify-between", children: [_jsx(SectionMarker, { num: `S—${(session.currentIndex + 1).toString().padStart(2, '0')}`, label: t(cat.labelKey, { ns: 'library' }) }), _jsx("button", { type: "button", onClick: onAbandon, className: "\n            font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-text-secondary\n            transition hover:text-cyan-core\n          ", children: t('session:player.abandon') })] }), _jsxs("div", { className: "relative flex flex-col items-center gap-4 pt-6", children: [_jsx(CoordinateMarker, { prefix: "J", num: (session.currentIndex + 1).toString().padStart(2, '0'), suffix: `STEP ${session.currentIndex + 1}/${session.items.length}`, className: "absolute right-0 top-0" }), _jsx(CategoryIcon, { slug: cat.slug, size: 80 }), _jsx("p", { className: "font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-cyan-network", children: t(cat.labelKey, { ns: 'library' }) }), _jsx("h1", { className: "text-center font-display text-[28px] font-medium leading-[1.05] tracking-cy-display text-cyan-text", children: humanizeSlug(current.slug) }), _jsx("p", { className: "max-w-[300px] text-center text-[13px] font-light leading-5 text-cyan-text-secondary", children: t('session:player.redirect_body') })] }), _jsx("div", { className: "mt-auto flex flex-col gap-3 pb-2", children: _jsx("p", { className: "text-center font-mono text-[9px] uppercase tracking-[0.15em] text-cyan-text-faint", children: t('session:player.progress', {
                        done: completedCount,
                        total: session.items.length,
                    }) }) }), _jsx("div", { "aria-hidden": true, className: "h-[120px]" }), _jsxs(StickyCta, { children: [_jsxs(Link, { to: `/play/${current.slug}`, className: "\n            inline-flex h-12 w-full items-center justify-center gap-2\n            bg-cyan-core text-cyan-surface\n            font-display text-[14px] font-medium tracking-[-0.01em]\n            transition active:scale-[0.985]\n            hover:bg-cyan-network\n          ", children: [t('session:player.go_to_game'), _jsx(ArrowRight, { size: 14, strokeWidth: 2, "aria-hidden": true })] }), _jsx("button", { type: "button", onClick: onSkip, className: "\n            inline-flex w-full items-center justify-center\n            font-mono text-[11px] uppercase tracking-[0.15em] text-cyan-text-secondary\n            transition hover:text-cyan-core\n          ", children: t('session:intermission.skip') })] })] }));
}
//# sourceMappingURL=SessionPlayer.js.map